/**
 * `/` — the landing screen every session hits first.
 *
 * Two different screens live behind one address: a platform administrator sees
 * the estate, everyone else sees their own work. Rendered eagerly, because
 * this is the first thing loaded.
 *
 * @module router/OverviewRoute
 */

import React, { useCallback } from "react";

import { useNavigate } from "react-router";

import { AttentionQueue } from "@/components/overview/AttentionQueue";
import { moduleReviewRows } from "@/components/overview/module-review-rows";
import { ModuleReviewTable } from "@/components/overview/ModuleReviewTable";
import { PlatformOperations } from "@/components/overview/PlatformOperations";
import { PortfolioMetrics } from "@/components/overview/PortfolioMetrics";
import { ProgrammeDirectory } from "@/components/overview/ProgrammeDirectory";
import {
  getBackendDashboardSections,
  LEAD_ATTENTION_STATES,
  roleProgrammeQueue,
} from "@/components/shared/workflow-queue-logic";
import { PageHeader } from "@/components/ui";

import { useWorkspaceOutletContext } from "./workspace-context";

export function OverviewRoute(): React.ReactElement {
  const navigate = useNavigate();
  const {
    session,
    platformProgrammes,
    platformModules,
    platformUsers,
    platformPortfolio,
    platformAdminOverview,
    platformDataError,
    isPlatformAdmin,
    canAccessWorkspace,
  } = useWorkspaceOutletContext();

  const openProgramme = useCallback(
    (programmeId: string, moduleId?: string) => {
      navigate(
        moduleId
          ? `/programmes/${programmeId}/modules/${moduleId}`
          : `/programmes/${programmeId}/governance`,
      );
    },
    [navigate],
  );

  if (isPlatformAdmin) {
    return (
      <div className="flex flex-col gap-6" data-testid="platform-overview">
        <PageHeader
          title="Platform operations"
          description="See all tenants and the work still to finish."
        />
        <PlatformOperations overview={platformAdminOverview} error={platformDataError} />
      </div>
    );
  }

  const isClientAdmin = session.subject.accountRole === "client-admin";
  const reviewRows = moduleReviewRows(session, platformProgrammes, platformModules);
  const decidesModuleReviews =
    isClientAdmin ||
    session.subject.responsibilities.some((assignment) => assignment.responsibility === "lead");
  const open = canAccessWorkspace ? openProgramme : undefined;
  // A queue item opens where its work is: setup and development on Overview,
  // review and approval on Governance, where the directory also goes.
  const openAttention =
    open &&
    ((programmeId: string, moduleId?: string) => {
      const state = platformProgrammes.find(
        (programme) => programme.id === programmeId,
      )?.workflowState;
      if (!moduleId && state && LEAD_ATTENTION_STATES.has(state)) {
        navigate(`/programmes/${programmeId}/overview`);
        return;
      }
      open(programmeId, moduleId);
    });

  return (
    <div className="flex flex-col gap-6" data-testid="overview">
      <PageHeader
        title="Overview"
        description="See what needs your attention and the status of your programmes."
      />

      <AttentionQueue
        sections={getBackendDashboardSections(session, platformProgrammes, platformModules)}
        error={platformDataError}
        onOpen={openAttention}
      />

      {/* Whether the reader leads anything is in the session, so the panel
          holds its place while the counts load rather than appearing after. */}
      {decidesModuleReviews && (
        <ModuleReviewTable
          rows={reviewRows}
          error={platformDataError}
          administers={isClientAdmin}
        />
      )}

      {isClientAdmin && (
        <PortfolioMetrics
          portfolio={platformPortfolio}
          programmes={platformProgrammes}
          users={platformUsers}
          error={platformDataError}
          onOpenProgramme={open ? (programmeId) => open(programmeId) : undefined}
          tenantName={session.tenant.name}
        />
      )}

      <ProgrammeDirectory
        title={isClientAdmin ? "Programme directory" : "Your programmes and modules"}
        programmes={roleProgrammeQueue(session, platformProgrammes)}
        modules={isClientAdmin ? undefined : platformModules}
        currentUserId={session.subject.userId}
        ledProgrammeIds={session.subject.responsibilities
          .filter((assignment) => assignment.responsibility === "lead")
          .map((assignment) => assignment.programmeId)}
        error={platformDataError}
        onOpen={open ? (programmeId) => open(programmeId) : undefined}
      />
    </div>
  );
}
